import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { Badge } from '@/components/shadcn/badge'

import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { getInitials } from '@/lib/utils'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

// Validation schema
const formSchema = z.object({
  selectedAdminIds: z.array(z.number()).min(1, 'Please select at least one admin to add'),
})

export type MaintainPlatformAdminsForm = z.infer<typeof formSchema>

// type MessagesSubscriptionsOrganisationSummary = RestSchemas['Messages.Subscriptions.OrganisationSummary']
type MessagesApplicationsUser = RestSchemas['Messages.Applications.User']

type MaintainPlatformAdminsV2Props = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  orgId?: number
}

export const MaintainPlatformAdminsV2 = (props: MaintainPlatformAdminsV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainPlatformAdminsV2Props) => {
  const { id: orgId } = useCurrentOrganisation(props.orgId)

  const {
    getSubscriptionsApiListAllPlatformAdmins,
    getSubscriptionsApiListOrganisationPlatformAdmins,
    putSubscriptionsApiAddPlatformAdmin,
    deleteSubscriptionsApiRemovePlatformAdmin,
  } = useRestClient()

  const {
    data: allAdmins,
    isPending: allAdminsLoading,
    isError: allAdminsError,
  } = getSubscriptionsApiListAllPlatformAdmins.useQuery()

  const {
    data: currentAdmins,
    isPending: currentAdminsLoading,
    isError: currentAdminsError,
  } = getSubscriptionsApiListOrganisationPlatformAdmins.useQuery({
    query: { organisationId: orgId },
  })

  const invalidateCurrentAdmins = () => {
    getSubscriptionsApiListOrganisationPlatformAdmins.invalidateQueries({
      parameters: { query: { organisationId: orgId } },
    })
  }

  const { mutateAsync: addPlatformAdmin } = putSubscriptionsApiAddPlatformAdmin.useMutation(undefined, {
    onSuccess: () => {
      invalidateCurrentAdmins()
      toast.success('Admins successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding admins')
    },
  })

  const { mutate: deletePlatformAdmin } = deleteSubscriptionsApiRemovePlatformAdmin.useMutation(undefined, {
    onSuccess: () => {
      invalidateCurrentAdmins()
      toast.success('Admin successfully removed')
    },
    onError: () => {
      toast.error('Error removing admin')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedAdminIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainPlatformAdminsForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Helper: user display name
  const getUserDisplayName = (user: Pick<MessagesApplicationsUser, 'givenName' | 'familyName'>): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : 'Unknown User'
  }

  // Build options from all admins, excluding current org admins, with disambiguation
  const adminOptions: SelectOption<number>[] = useMemo(() => {
    if (!allAdmins) return []

    const sorted = [...allAdmins].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    const currentIds = new Set((currentAdmins ?? []).map((u) => u.id))

    const nameCounts: Record<string, number> = {}
    sorted.forEach((u) => {
      const base = getUserDisplayName(u)
      nameCounts[base] = (nameCounts[base] || 0) + 1
    })

    return sorted
      .filter((u) => !currentIds.has(u.id))
      .map((u) => {
        const base = getUserDisplayName(u)
        const label = nameCounts[base] > 1 ? `${base} (ID: ${u.id})` : base
        return { value: u.id, label }
      })
  }, [allAdmins, currentAdmins])

  const handleSubmit = async (data: MaintainPlatformAdminsForm) => {
    const promises = data.selectedAdminIds.map((adminId) =>
      addPlatformAdmin({ query: { organisationId: orgId, adminId } })
    )
    await Promise.all(promises)
    form.reset()
  }

  const handleDeleteAdmin = (adminId: number) => {
    deletePlatformAdmin({ query: { organisationId: orgId, adminId } })
  }

  if (allAdminsLoading || currentAdminsLoading) return <FormComponentLoading />
  if (allAdminsError || currentAdminsError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedAdminIds"
                label="Add Platform Admins"
                options={adminOptions}
                placeholder="Select platform admins to add..."
                searchPlaceholder="Search platform admins..."
                emptyMessage="No available platform admins found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={adminOptions.length === 0}>
                Add Selected Admins
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {(currentAdmins ?? []).length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Platform Admins:</div>
          <div className="space-y-2">
            {(currentAdmins ?? []).map((admin) => (
              <PlatformAdminCard key={admin.id} admin={admin} onDelete={() => handleDeleteAdmin(admin.id)} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

const PlatformAdminCard = ({ admin, onDelete }: { admin: MessagesApplicationsUser; onDelete: () => void }) => {
  const name =
    admin.givenName || admin.familyName ? `${admin.givenName ?? ''} ${admin.familyName ?? ''}`.trim() : 'Unknown User'

  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">{getInitials(name)}</AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{name}</span>
          <Badge variant="outline">{admin.id}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
